/*
 * Raw Kubernetes objects as a cluster returns them, for the projection's tests
 * (IM-205). Synthetic, written from the API's shapes, because a real cluster's
 * objects carry a customer's names and a fixture is committed forever.
 *
 * Secrets are seeded everywhere a real one turns up: an env value, `envFrom`,
 * an argument list, an exec probe, an HTTP probe's header, an annotation, a
 * label, a volume, a KEDA trigger's connection string and an Alertmanager
 * receiver's webhook. None of them may reach the projection's output.
 */
/** Realistic in shape, worthless in fact. */
export const CLUSTER_SECRETS = {
    envPassword: 'Tr0ub4dor-env-example',
    argToken: 'swordfish-arg-token-example',
    probeToken: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJwcm9iZSJ9.c2lnbmF0dXJlZXhhbXBsZQ',
    annotation: 'AKIAIOSFODNN7EXAMPLE',
    amqp: 'amqp://qrvey:hunter2-example@rabbitmq.qrvey.svc:5672/',
    webhook: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX',
    eventPassword: 'correct-horse-event-example',
};
const S = CLUSTER_SECRETS;
const conditionsReady = [
    { type: 'Ready', status: 'True', lastTransitionTime: '2026-10-28T10:00:00Z' },
];
export const RAW_CLUSTER = {
    nodes: [
        {
            metadata: {
                name: 'ip-10-0-1-10.us-west-2.compute.internal',
                creationTimestamp: '2026-10-20T08:00:00Z',
                labels: {
                    'karpenter.sh/nodepool': 'app',
                    'karpenter.sh/capacity-type': 'on-demand',
                    'node.kubernetes.io/instance-type': 't4g.large',
                    'topology.kubernetes.io/zone': 'us-west-2a',
                    'team-secret': S.annotation,
                },
                annotations: { 'karpenter.sh/nodeclaim-token': S.annotation },
            },
            spec: { taints: [{ key: 'dedicated', value: S.argToken, effect: 'NoSchedule' }] },
            status: {
                allocatable: { cpu: '1930m', memory: '7Gi', pods: '110' },
                capacity: { cpu: '2', memory: '8Gi', pods: '110' },
                conditions: [
                    ...conditionsReady,
                    { type: 'MemoryPressure', status: 'False', reason: 'KubeletHasSufficientMemory' },
                ],
                nodeInfo: { kubeletVersion: 'v1.33.4-eks-1' },
                addresses: [{ type: 'InternalIP', address: '10.0.1.10' }],
            },
        },
    ],
    pods: [
        {
            metadata: {
                name: 'dataload-7c9f-abcde',
                namespace: 'qrvey',
                creationTimestamp: '2026-10-28T09:00:00Z',
                labels: { app: 'dataload', secret: S.argToken },
                annotations: {
                    'kubectl.kubernetes.io/last-applied-configuration': `{"env":[{"value":"${S.envPassword}"}]}`,
                    'vault.hashicorp.com/token': S.annotation,
                },
                ownerReferences: [{ kind: 'ReplicaSet', name: 'dataload-7c9f', controller: true }],
                managedFields: [{ manager: 'kubectl', fieldsV1: { 'f:spec': {} } }],
            },
            spec: {
                nodeName: 'ip-10-0-1-10.us-west-2.compute.internal',
                priorityClassName: 'qrvey-high',
                containers: [
                    {
                        name: 'dataload',
                        image: 'qrvey.azurecr.io/dataload:9.6.0',
                        command: ['/bin/sh', '-c', `export TOKEN=${S.argToken}; run`],
                        args: ['--password', S.envPassword, `--token=${S.argToken}`],
                        env: [
                            { name: 'DB_PASSWORD', value: S.envPassword },
                            { name: 'API_TOKEN', valueFrom: { secretKeyRef: { name: 'api', key: 'token' } } },
                        ],
                        envFrom: [{ secretRef: { name: 'dataload-secrets' } }],
                        resources: { requests: { cpu: '500m', memory: '1Gi' }, limits: { memory: '2Gi' } },
                        livenessProbe: {
                            httpGet: {
                                path: '/health',
                                port: 8080,
                                httpHeaders: [{ name: 'Authorization', value: S.probeToken }],
                            },
                            periodSeconds: 10,
                            failureThreshold: 3,
                        },
                        readinessProbe: {
                            exec: { command: ['check', `--secret=${S.argToken}`] },
                            initialDelaySeconds: 5,
                        },
                        volumeMounts: [{ name: 'creds', mountPath: '/etc/creds' }],
                    },
                ],
                volumes: [{ name: 'creds', secret: { secretName: 'dataload-creds' } }],
            },
            status: {
                phase: 'Running',
                qosClass: 'Burstable',
                startTime: '2026-10-28T09:00:05Z',
                conditions: conditionsReady,
                containerStatuses: [
                    {
                        name: 'dataload',
                        ready: true,
                        restartCount: 4,
                        state: { running: { startedAt: '2026-10-28T09:30:00Z' } },
                        lastState: {
                            terminated: {
                                reason: 'OOMKilled',
                                exitCode: 137,
                                finishedAt: '2026-10-28T09:29:00Z',
                            },
                        },
                    },
                ],
            },
        },
        {
            metadata: {
                name: 'pending-1',
                namespace: 'qrvey',
                creationTimestamp: '2026-10-28T11:00:00Z',
            },
            spec: { containers: [{ name: 'app', image: 'qrvey.azurecr.io/app:9.6.0' }] },
            status: {
                phase: 'Pending',
                conditions: [
                    {
                        type: 'PodScheduled',
                        status: 'False',
                        reason: 'Unschedulable',
                        message: '0/10 nodes are available: 10 Insufficient cpu.',
                    },
                ],
            },
        },
    ],
    events: [
        {
            type: 'Warning',
            reason: 'FailedScheduling',
            involvedObject: { kind: 'Pod', name: 'pending-1', namespace: 'qrvey' },
            count: 12,
            lastTimestamp: '2026-10-28T11:05:00Z',
            message: '0/10 nodes are available: 10 Insufficient cpu.',
            source: { component: 'default-scheduler' },
        },
        {
            type: 'Normal',
            reason: 'Pulled',
            involvedObject: { kind: 'Pod', name: 'dataload-7c9f-abcde', namespace: 'qrvey' },
            lastTimestamp: '2026-10-28T09:00:10Z',
            message: `connection string postgres://qrvey:${S.eventPassword}@db.internal:5432/qrvey password=${S.eventPassword}`,
        },
    ],
    deployments: [
        {
            metadata: { name: 'dataload', namespace: 'qrvey' },
            spec: {
                replicas: 3,
                template: {
                    spec: {
                        containers: [
                            {
                                name: 'dataload',
                                image: 'qrvey.azurecr.io/dataload:9.6.0',
                                env: [{ name: 'DB_PASSWORD', value: S.envPassword }],
                                args: [S.argToken],
                                resources: { requests: { cpu: '500m' } },
                            },
                        ],
                    },
                },
            },
            status: {
                readyReplicas: 2,
                availableReplicas: 2,
                updatedReplicas: 3,
                conditions: conditionsReady,
            },
        },
    ],
    statefulSets: [],
    daemonSets: [
        {
            metadata: { name: 'aws-node', namespace: 'kube-system' },
            spec: {
                template: { spec: { containers: [{ name: 'aws-node', image: 'amazon-k8s-cni:v1.19' }] } },
            },
            status: {
                desiredNumberScheduled: 10,
                numberReady: 10,
                numberAvailable: 10,
                updatedNumberScheduled: 10,
            },
        },
    ],
    jobs: [],
    horizontalPodAutoscalers: [
        {
            metadata: { name: 'keda-hpa-dataload', namespace: 'qrvey' },
            spec: {
                scaleTargetRef: { kind: 'Deployment', name: 'dataload' },
                minReplicas: 1,
                maxReplicas: 20,
                metrics: [
                    {
                        type: 'Resource',
                        resource: { name: 'cpu', target: { type: 'Utilization', averageUtilization: 70 } },
                    },
                    {
                        type: 'External',
                        external: {
                            metric: { name: 's0-rabbitmq-dataload' },
                            target: { type: 'AverageValue', averageValue: '5' },
                        },
                    },
                ],
            },
            status: { currentReplicas: 3, desiredReplicas: 7, lastScaleTime: '2026-10-28T11:00:00Z' },
        },
    ],
    podDisruptionBudgets: [],
    persistentVolumeClaims: [
        {
            metadata: { name: 'prometheus-db', namespace: 'monitoring' },
            spec: { storageClassName: 'gp3', resources: { requests: { storage: '50Gi' } } },
            status: { phase: 'Bound', capacity: { storage: '50Gi' } },
        },
    ],
    nodePools: [
        {
            metadata: { name: 'app' },
            spec: {
                weight: 10,
                limits: { cpu: '40', memory: '160Gi' },
                disruption: {
                    consolidationPolicy: 'WhenEmptyOrUnderutilized',
                    consolidateAfter: '1m',
                    budgets: [{ nodes: '10%' }],
                },
                template: {
                    spec: {
                        nodeClassRef: { group: 'karpenter.k8s.aws', kind: 'EC2NodeClass', name: 'default' },
                        requirements: [
                            {
                                key: 'node.kubernetes.io/instance-type',
                                operator: 'In',
                                values: ['t4g.large', 'm6g.large'],
                            },
                        ],
                    },
                },
            },
            status: {
                resources: { cpu: '40', memory: '80Gi', nodes: '10' },
                conditions: conditionsReady,
            },
        },
    ],
    nodeClaims: [],
    nodeClasses: [
        {
            metadata: { name: 'default' },
            spec: {
                amiSelectorTerms: [{ alias: 'al2023@latest' }],
                subnetSelectorTerms: [{ tags: { 'karpenter.sh/discovery': 'qrvey-eks-demo' } }],
                blockDeviceMappings: [
                    { deviceName: '/dev/xvda', ebs: { volumeSize: '50Gi', volumeType: 'gp3' } },
                ],
                userData: `#!/bin/bash\necho ${S.argToken}`,
            },
            status: { subnets: [{ id: 'subnet-0abc', zone: 'us-west-2a' }] },
        },
    ],
    scaledObjects: [
        {
            metadata: { name: 'dataload', namespace: 'qrvey' },
            spec: {
                scaleTargetRef: { name: 'dataload' },
                minReplicaCount: 1,
                maxReplicaCount: 20,
                pollingInterval: 5,
                cooldownPeriod: 30,
                triggers: [
                    {
                        type: 'rabbitmq',
                        metadata: {
                            host: S.amqp,
                            queueName: 'dataload',
                            mode: 'QueueLength',
                            value: '5',
                            password: S.envPassword,
                        },
                        authenticationRef: { name: 'rabbitmq-auth' },
                    },
                ],
            },
            status: { lastActiveTime: '2026-10-28T11:00:00Z', conditions: conditionsReady },
        },
    ],
    scaledJobs: [],
    clusterQueues: [
        {
            metadata: { name: 'sync' },
            spec: {
                queueingStrategy: 'BestEffortFIFO',
                resourceGroups: [
                    {
                        coveredResources: ['cpu', 'memory'],
                        flavors: [
                            {
                                name: 'default',
                                resources: [
                                    { name: 'cpu', nominalQuota: '8' },
                                    { name: 'memory', nominalQuota: '32Gi' },
                                ],
                            },
                        ],
                    },
                ],
            },
            status: {
                pendingWorkloads: 14,
                admittedWorkloads: 4,
                flavorsUsage: [{ name: 'default', resources: [{ name: 'cpu', total: '8' }] }],
            },
        },
    ],
    localQueues: [],
    workloads: [],
    prometheuses: [
        {
            metadata: { name: 'kube-prometheus', namespace: 'monitoring' },
            spec: {
                replicas: 1,
                retention: '10d',
                resources: { requests: { memory: '2Gi' }, limits: { memory: '4Gi' } },
                storage: {
                    volumeClaimTemplate: { spec: { resources: { requests: { storage: '50Gi' } } } },
                },
                remoteWrite: [{ url: S.webhook, basicAuth: { password: { name: 'x', key: 'y' } } }],
            },
            status: { availableReplicas: 1 },
        },
    ],
    alertmanagers: [],
    alertmanagerConfigs: [
        {
            metadata: { name: 'qrvey', namespace: 'monitoring' },
            spec: {
                route: { receiver: 'oncall' },
                receivers: [
                    {
                        name: 'oncall',
                        slackConfigs: [{ apiURL: { name: 'slack', key: 'url' }, channel: '#oncall' }],
                    },
                    { name: 'hooks', webhookConfigs: [{ url: S.webhook }] },
                    { name: 'null' },
                ],
            },
        },
    ],
    nodeMetrics: [
        {
            metadata: { name: 'ip-10-0-1-10.us-west-2.compute.internal' },
            timestamp: '2026-10-28T11:05:00Z',
            usage: { cpu: '1800m', memory: '6Gi' },
        },
    ],
    podMetrics: [
        {
            metadata: { name: 'dataload-7c9f-abcde', namespace: 'qrvey' },
            containers: [{ name: 'dataload', usage: { cpu: '450m', memory: '1900Mi' } }],
        },
    ],
};
const vector = (...results) => JSON.stringify({
    status: 'success',
    data: {
        resultType: 'vector',
        result: results.map((r) => ({ metric: r.metric, value: [1793652894.024, r.value] })),
    },
});
/**
 * Prometheus's answers as its HTTP API returns them (IM-207), one body per
 * query. An exporter can put anything in a label, so secrets are seeded in a
 * label no check reads — which must be dropped — and in one a check does
 * read, which must be masked.
 */
export const RAW_PROMETHEUS = {
    queries: {
        headSeries: vector({ metric: {}, value: '781096' }),
        headSeriesPeak: vector({ metric: {}, value: '790000' }),
        targetsByJob: vector({ metric: { job: 'kubelet' }, value: '21' }, { metric: { job: 'rabbitmq-builtin' }, value: '1' }),
        targetsDown: vector({
            metric: {
                __name__: 'up',
                job: 'rabbitmq-builtin',
                instance: S.amqp,
                password: S.envPassword,
                authorization: S.probeToken,
            },
            value: '0',
        }),
        kongRequestsHour: vector({ metric: {}, value: '2337.47' }),
        kongErrorsHour: vector({ metric: {}, value: '0' }),
        kongRequestsDay: vector({ metric: {}, value: '51000' }),
        kongErrorsDay: vector({ metric: {}, value: 'NaN' }),
        kongErrorServices: vector({
            metric: { service: 'httproute.qrveyapps.qrvey-route-public-widgets.0', token: S.argToken },
            value: '12',
        }),
    },
    tsdb: JSON.stringify({
        status: 'success',
        data: {
            headStats: { numSeries: 781096, chunkCount: 1118805 },
            seriesCountByMetricName: [{ name: 'rabbitmq_channel_messages_unacked', value: 72767 }],
            seriesCountByLabelValuePair: [
                { name: 'namespace=rabbitmq', value: 589184 },
                { name: `instance=${S.amqp}`, value: 585513 },
            ],
            memoryInBytesByLabelName: [{ name: 'instance', value: 37049236 }],
        },
    }),
};
//# sourceMappingURL=cluster-objects.js.map